// @tg-seq: 10009
// @architect-approved: HARDCODED_COLORS - Directory pattern requires translucent white overlays for header subtitle and component watermark branding
/**
 * Access Badge Reader — RFID Gate Directory (Host)
 *
 * ARCHITECTURE: Host component that reads the manifest for background, header,
 * subtitle, and destination — then renders DirmReadPattern (the PIN pad + RFID
 * auth widget) as frosted-glass cards on the right side.
 *
 * pattern_id: 9 (READ)
 * Database Dirs_Sequence_Num: 10009 → Directory_Registry_v2 dir_seq 17
 * Single-assignment directory: auth success → navigate to button target.
 *
 * Layout mirrors com_tgc_10023 (receptionist): full background image + dark
 * overlay, centered header/subtitle, DirmReadPattern fixed-right cards.
 *
 * Created: 2026-03-22
 * Converted: 2026-04-05 — DirmReadPattern badge reader
 * Refactored: 2026-04-06 — host + auth widget separation
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BadgeIcon from '@mui/icons-material/Badge';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import HomeIcon from '@mui/icons-material/Home';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    Alert,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';

import { getTsxFileName } from '../../utils/componentIdentity';
import { DirmReadPattern } from '../ComponentTemplates/DirectoryPatterns';
import AccessRequestModal from '../RegisteredTabs/scr_tgc_10849_quick_help_atm';
import { useDirectoryHost } from '../ComponentTemplates/DirectoryPatterns/useDirectoryHost';
// @architect-approved: DIRECTORY_HOST_PATTERN - useDirectoryManifest needed for loading OTHER directories (elevator, button targets, fallbacks)
// eslint-disable-next-line no-restricted-imports
import { useDirectoryManifest } from '../ComponentTemplates/DirectoryPatterns/useDirectoryManifest';
import backgroundImg from '../img/bg_tgc_dir_17.png';

import generatedRoutes from '../../generated-routes.json';
import { resolveComponentBySeq } from '../../utils/componentResolver';
import { handleManifestButtonClick } from '../../utils/directoryNavigation';
import { evaluateTumblerAccess } from '../../utils/tumblerAccess';
import { useAuth } from '../../utils/useAuth';
import { useNavigationChrome } from '../../utils/useNavigationChrome';

// Build sequence → route lookup map for fallback resolution
const routesBySeq = new Map<number, any>();
(generatedRoutes as any).routes.forEach((route: any) => {
    if (route.seq) {
        routesBySeq.set(route.seq, route);
    }
});

// Component declarations (sequence-driven resolution)
const ReceptionLogin = resolveComponentBySeq(10037);
const ElevatorDirSeq = 2;

// Floor 2 Public Collaboration Spaces (no auth required — visibility_auth_tier: 0)
// Destination for authenticated users who lack the org association / role needed
// for this access point's floor (3+), so they aren't stranded retrying the badge reader.
const COLLABORATION_CENTER_ROUTE = '/nav_tgc_10000_dirm_directory_host';
const COLLABORATION_CENTER_DIR_SEQ = 10;

const Self: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const chrome = useNavigationChrome('gate', 10009); // Database-driven gate chrome

    /* ---- Manifest resolution (MUST come before video integration) ---- */
    // @architect-approved: DIRECTORY_HOST_PATTERN - useDirectoryHost encapsulates target_dir_seq resolution
    // dir = the ACCESS DIRECTORY being hosted (17, 18, 191, 193, 194, 197, etc.)
    // This is passed via location.state.dirSeq when routing from elevator button
    // Elevator button has: Target_Sequence_Num=10009 (this host), Target_Dir_Sequence_Num=17 (access dir)
    const dir = useDirectoryHost(10009);

    /* ---- Access request modal state ---- */
    const [showAccessRequestModal, setShowAccessRequestModal] = useState(false);

    /* ---- Login overlay state ---- */
    const [showLoginOverlay, setShowLoginOverlay] = useState(false);

    /* ---- Access denied modal state ---- */
    const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);

    /*  Auth check state  */
    const [isCheckingAuth] = useState(false); // Always false - skip loading screen
    const [needsAuth] = useState(true); // Always true - always show badge reader

    // Resolve current directory route for backTo navigation
    const currentDirRoute = useMemo(() => {
        if (dir?.componentSequenceNum) {
            const routeData = routesBySeq.get(dir.componentSequenceNum);
            if (routeData?.directRoute) {
                return routeData.directRoute;
            }
        }
        // Legacy fallback if tsxFileName exists
        if (dir?.tsxFileName) {
            return '/' + dir.tsxFileName.replace(/\.tsx$/, '');
        }
        return '/'; // Fallback to root
    }, [dir]);

    // Elevator fallback
    const elevatorDirectory = useDirectoryManifest(ElevatorDirSeq);

    /* ---- Button registry (used for fallback only when no dirSeq in routing state) ---- */
    const target = dir?.buttons?.slice().sort((a, b) => a.displayOrder - b.displayOrder)[0] ?? null;
    const buttonTargetDirectory = useDirectoryManifest(target?.target_dir_sequence_num ?? -1);
    const assignmentFallbackDirSeq = target?.fallbackDirSequenceNum ?? null;
    const directoryFallbackDirSeq = dir?.fallbackDirSequenceNum ?? null;
    const fallbackDirSeq = assignmentFallbackDirSeq ?? directoryFallbackDirSeq ?? ElevatorDirSeq;
    const fallbackDirectoryByManifest = useDirectoryManifest(fallbackDirSeq ?? -1);

    const fallbackRoute = useMemo(() => {
        // Resolve fallback route from directory's componentSequenceNum
        // Manifest structure changed 2026-06-27: tsxFileName removed, use componentSequenceNum → generated-routes lookup
        if (fallbackDirectoryByManifest?.componentSequenceNum) {
            const routeData = routesBySeq.get(fallbackDirectoryByManifest.componentSequenceNum);
            if (routeData?.directRoute) {
                return routeData.directRoute;
            }
        }
        if (elevatorDirectory?.componentSequenceNum) {
            const routeData = routesBySeq.get(elevatorDirectory.componentSequenceNum);
            if (routeData?.directRoute) {
                return routeData.directRoute;
            }
        }
        // Legacy fallback if tsxFileName exists (backward compatibility)
        if (fallbackDirectoryByManifest?.tsxFileName) {
            return '/' + fallbackDirectoryByManifest.tsxFileName.replace(/\.tsx$/, '');
        }
        if (elevatorDirectory?.tsxFileName) {
            return '/' + elevatorDirectory.tsxFileName.replace(/\.tsx$/, '');
        }
        return null;
    }, [fallbackDirectoryByManifest, elevatorDirectory]);

    const fallbackDirName =
        target?.fallbackDirectoryName ||
        dir?.fallbackDirectoryName ||
        fallbackDirectoryByManifest?.headerText ||
        fallbackDirectoryByManifest?.directoryName ||
        'Elevator';

    const navigateToFallback = (reason: string) => {
        if (!fallbackRoute) {
            console.error(
                `[Access Badge Reader] Unable to resolve fallback route for dir seq ${fallbackDirSeq ?? ElevatorDirSeq}`,
            );
            navigate(-1);
            return;
        }

        console.warn(
            `[Access Badge Reader] Redirecting to fallback directory (${fallbackDirName}) due to: ${reason}`,
        );

        navigate(fallbackRoute, {
            state: {
                dirSeq: fallbackDirSeq ?? ElevatorDirSeq,
                backTo: currentDirRoute,
            },
        });
    };

    /* ---- Back navigation ---- */
    const backTo = (location.state as { backTo?: string } | null)?.backTo;
    const handleBack = () => {
        if (backTo) navigate(backTo);
        else navigate(-1);
    };

    /* ---- Auth success → navigate to target ---- */
    const handleAuthenticated = () => {
        console.log('[Badge Reader 10009] ===== handleAuthenticated START =====');
        console.log(
            '[Badge Reader 10009] Access directory sequence (from useDirectoryHost):',
            dir?.directorySequenceNum,
        );
        console.log('[Badge Reader 10009] hasTarget:', !!target);
        console.log('[Badge Reader 10009] target object:', JSON.stringify(target, null, 2));
        console.log('[Badge Reader 10009] dir.directorySequenceNum:', dir?.directorySequenceNum);
        console.log('[Badge Reader 10009] currentDirRoute:', currentDirRoute);

        // ✅ FIX: Use access directory's button assignment (loaded from dir manifest via useDirectoryHost)
        // dir.directorySequenceNum (e.g., 17) = THIS access directory
        // target.target_dir_sequence_num (e.g., 189) = floor directory to navigate to AFTER auth
        if (target) {
            console.log('[Badge Reader 10009] ✅ Navigating to floor via button assignment');
            console.log(
                '[Badge Reader 10009] Button target_dir_sequence_num:',
                target.target_dir_sequence_num,
            );
            console.log('[Badge Reader 10009] Button displayLabel:', target.displayLabel);

            // ✅ Tumbler pin combination lock — same mechanism every Gate-prefixed
            // host calls, regardless of how credentials were captured upstream.
            const tumblerResult = evaluateTumblerAccess(target, auth);
            if (!tumblerResult.allowed) {
                console.warn(
                    '[Badge Reader 10009] Tumbler access denied:',
                    tumblerResult.failedPin,
                    tumblerResult.reason,
                );
                handleAccessDenied(tumblerResult.reason ?? 'AUTH_FAILED');
                return;
            }

            console.log('[Badge Reader 10009] Calling handleManifestButtonClick...');
            handleManifestButtonClick(target, navigate, dir?.directorySequenceNum, currentDirRoute);
            console.log(
                '[Badge Reader 10009] handleManifestButtonClick returned (should have navigated)',
            );
            return;
        }

        // No target found
        console.error('[Badge Reader 10009] ❌ No target button configured');
        console.error('[Badge Reader 10009] Calling navigateToFallback...');
        navigateToFallback('no target button configured after authentication');
    };

    /* ---- Access denied ---- */
    const handleAccessDenied = (
        reason:
            | 'NO_ORG_ASSOCIATION'
            | 'AUTH_FAILED'
            | 'INSUFFICIENT_AUTH_TIER'
            // Tumbler pin denial reasons (evaluateTumblerAccess) — mapped onto the
            // same two existing UX paths below rather than new ones.
            | 'JWT_REQUIRED'
            | 'ORG_ASSOCIATION_REQUIRED'
            | 'DEPT_VALIDATION_FAILED'
            | 'INSUFFICIENT_HIERARCHY'
            | 'SUPERADMIN_REQUIRED',
    ) => {
        console.error('[Access Badge Reader] Access denied:', reason);

        // Authenticated user, but no org association / insufficient role yet (floors 3+).
        // Don't strand them retrying the badge reader — send them somewhere they CAN go
        // (Collaboration Center) with a clear explanation, instead of showing the generic
        // login/signup modal (which is for unauthenticated visitors, not this case).
        if (
            reason === 'NO_ORG_ASSOCIATION' ||
            reason === 'INSUFFICIENT_AUTH_TIER' ||
            reason === 'ORG_ASSOCIATION_REQUIRED' ||
            reason === 'DEPT_VALIDATION_FAILED' ||
            reason === 'INSUFFICIENT_HIERARCHY'
        ) {
            toast.info(
                "You don't have organization access to this floor yet — taking you to the " +
                    'Collaboration Center for now. Once your organization association and role ' +
                    "are assigned, you'll be able to reach additional floors. Enjoy the visit — " +
                    'check out the Collaboration Center, explore public areas, or use the Welcome ' +
                    'Directory / elevator to get around.',
                { autoClose: 9000 },
            );
            navigate(COLLABORATION_CENTER_ROUTE, {
                state: {
                    dirSeq: COLLABORATION_CENTER_DIR_SEQ,
                    backTo: currentDirRoute,
                },
            });
            return;
        }

        setShowAccessDeniedModal(true);
    };

    /* ---- Manifest not found fallback ---- */
    if (!dir) {
        return (
            <Container maxWidth="md" sx={{ mt: 8 }}>
                <Typography color="error">
                    Access directory not found. Access directories must be navigated to via button
                    (requires dirSeq in route state).
                </Typography>
                <Typography sx={{ mt: 2 }}>Expected dirSeq: 10009</Typography>
                <Button onClick={() => navigate('/nav_tgc_10002_default_elevator')}>
                    Return to Elevator
                </Button>
            </Container>
        );
    }

    /* ---- Show loading while checking authentication ---- */
    if (isCheckingAuth) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundImage: `url(${backgroundImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                        Checking authentication...
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Use target directory's header text and description (where user is going)
    const targetDir = dir || buttonTargetDirectory;
    const headerText =
        targetDir?.headerText ||
        target?.target_header_text ||
        target?.displayLabel ||
        'Badge Reader';
    const subtitle =
        targetDir?.headerSubtitle ||
        targetDir?.directoryDescription ||
        target?.displayDescription ||
        'Tap your RFID badge or enter your PIN to authenticate';

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                position: 'relative',
                py: 6,
                /* Background image — static import (Vite hashed) */
                backgroundImage: `url(${backgroundImg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                /* Graphite rail overlay — matches Techgacity design system */
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: chrome.gate.overlay.backgroundColor,
                    zIndex: 1,
                },
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                {/* Back button */}
                <Box sx={{ mb: 4 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{
                            color: chrome.gate.overlay.textColor,
                            background: 'rgba(253, 253, 249, 0.1)',
                            border: '1px solid rgba(241, 236, 224, 0.2)',
                            '&:hover': {
                                background: 'rgba(253, 253, 249, 0.2)',
                                borderColor: 'rgba(241, 236, 224, 0.4)',
                            },
                        }}
                    >
                        Back
                    </Button>
                </Box>

                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <HowToRegIcon
                        sx={{ fontSize: 80, color: chrome.gate.overlay.textColor, mb: 2 }}
                    />
                    <Typography
                        variant="h2"
                        sx={{
                            color: chrome.gate.overlay.textColor,
                            fontWeight: 700,
                            mb: 2,
                            fontSize: '2.5rem',
                            textShadow: '0 4px 12px rgba(0,0,0,0.5)',
                        }}
                    >
                        {headerText}
                    </Typography>
                    {/* @architect-approved: HARDCODED_COLORS - Directory pattern subtitle requires translucent white overlay */}
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'rgba(241, 236, 224, 0.9)',
                            fontSize: '1.25rem',
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>

                {/* Destination label */}
                {target && (
                    <Box
                        sx={{
                            mt: 4,
                            p: 3,
                            background: 'rgba(253, 253, 249, 0.1)',
                            borderRadius: 2,
                            border: '2px solid rgba(241, 236, 224, 0.3)',
                            maxWidth: '60%',
                            mx: 'auto',
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: chrome.gate.overlay.textColor,
                                textAlign: 'center',
                                fontWeight: 500,
                                fontSize: '1.15rem',
                            }}
                        >
                            ℹ️ Authenticate to access: <strong>{headerText}</strong>
                        </Typography>
                    </Box>
                )}
            </Container>
            {/* ---- DirmReadPattern — PIN pad + RFID reader + help button (fixed right container) ---- */}
            {needsAuth && (
                <DirmReadPattern
                    onAuthenticated={handleAuthenticated}
                    on_access_denied={handleAccessDenied}
                    on_help_click={() => setShowAccessRequestModal(true)}
                    on_signup_click={() => navigate('/scr_tgc_10023_user_signup_kiosk')}
                    destinationLabel={headerText}
                    required_auth_tier={dir?.visibility_auth_tier ?? 0}
                />
            )}
            {/* ---- Access Request Modal — "Red Call Button" ---- */}
            <AccessRequestModal
                open={showAccessRequestModal}
                onClose={() => setShowAccessRequestModal(false)}
            />
            {/* ---- Login Overlay — compact auth for unauthenticated users ---- */}
            {showLoginOverlay && target && (
                <ReceptionLogin
                    onClose={() => setShowLoginOverlay(false)}
                    onLoginSuccess={() => {
                        setShowLoginOverlay(false);
                        // ✅ FIX: Navigate to floor directory (same logic as handleAuthenticated)
                        // Use button assignment directly - do NOT create synthetic button
                        if (target) {
                            handleManifestButtonClick(
                                target,
                                navigate,
                                dir?.directorySequenceNum,
                                currentDirRoute,
                            );
                        } else {
                            navigateToFallback('no target button after login overlay');
                        }
                    }}
                />
            )}
            {/* ---- Access Denied Modal — Options after failed badge scan ---- */}
            <Dialog
                open={showAccessDeniedModal}
                onClose={() => setShowAccessDeniedModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <BadgeIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                    <Typography variant="h6">Access Denied</Typography>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <strong>Authentication failed.</strong>
                        <br />
                        Your session may have expired or is invalid. Please sign in to continue.
                    </Alert>

                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            mb: 2,
                        }}
                    >
                        Choose an option to proceed:
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Quick Login Option */}
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<LoginIcon />}
                            onClick={() => {
                                setShowAccessDeniedModal(false);
                                setShowLoginOverlay(true);
                            }}
                            fullWidth
                        >
                            Quick Login (Username/Password)
                        </Button>

                        {/* Sign Up Option */}
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            startIcon={<PersonAddIcon />}
                            onClick={() => {
                                setShowAccessDeniedModal(false);
                                navigate('/scr_tgc_10023_user_signup_kiosk');
                            }}
                            fullWidth
                        >
                            I Need to Sign Up
                        </Button>

                        {/* Guest Access Option */}
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="large"
                            startIcon={<HelpOutlineIcon />}
                            onClick={() => {
                                setShowAccessDeniedModal(false);
                                setShowAccessRequestModal(true);
                            }}
                            fullWidth
                        >
                            I Need Guest Access (OTP)
                        </Button>

                        {/* Go to Welcome Option */}
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<HomeIcon />}
                            onClick={() => {
                                setShowAccessDeniedModal(false);
                                navigate('/welcome');
                            }}
                            fullWidth
                        >
                            Go to Welcome Page
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
                    <Button onClick={() => setShowAccessDeniedModal(false)} color="inherit">
                        Try Badge Again
                    </Button>
                    <Button
                        onClick={() => {
                            setShowAccessDeniedModal(false);
                            navigateToFallback('user chose elevator from access denied modal');
                        }}
                        variant="text"
                    >
                        Use Elevator
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Component identity watermark */}
            {/* @architect-approved: HARDCODED_COLORS - Component watermark requires low-opacity branding */}
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: 'rgba(241, 236, 224, 0.5)',
                    mt: 4,
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    position: 'relative',
                    zIndex: 2,
                }}
            >
                {dir
                    ? `${dir.directoryName} • seq:${dir.componentSequenceNum} • ${dir.tsxFileName}`
                    : 'DirmReadPattern · seq=10009'}
            </Typography>
        </Box>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
